import { Ionicons } from '@expo/vector-icons';
import React, { useMemo } from 'react';
import { View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { AppText } from '@/components/ui/AppText';
import { Card } from '@/components/ui/Card';
import { useThemePalette } from '@/config/theme';
import { useMatchXg } from '@/features/matches/hooks';
import type { MatchTrend } from '@/lib/data/provider';
import { localizeDigits } from '@/lib/i18n/format';
import { useLocale } from '@/lib/i18n/LocaleProvider';

/** Clamp a 0..1 hit-rate to a 0..100 integer percentage. */
function toPercent(value: number | null): number {
  if (value == null || !Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(100, Math.round(value * 100)));
}

/** One trend row: the form text, a thin Volt fill bar, and the % label. */
function TrendRow({ trend }: { trend: MatchTrend }) {
  const { arabicNumerals } = useLocale();
  const pct = toPercent(trend.percentage);
  return (
    <View className="gap-1.5">
      <View className="flex-row items-center justify-between gap-3">
        <AppText variant="caption" className="min-w-0 flex-1 leading-5 text-soft" numberOfLines={2}>
          {trend.text}
        </AppText>
        <AppText variant="caption" weight="bold" tabular className="shrink-0 text-volt-bright">
          {localizeDigits(`${pct}%`, arabicNumerals)}
        </AppText>
      </View>
      <View className="h-1.5 overflow-hidden rounded-full bg-white/10">
        <View className="h-full rounded-full bg-volt" style={{ width: `${Math.max(pct, 2)}%` }} />
      </View>
    </View>
  );
}

/**
 * Form Trends: recent-form insights for the fixture from 365scores
 * (`MatchXgStats.trends`). Each row is the provider's form text + a thin
 * percentage bar (hit rate, Volt fill) + the % label. Split into "General"
 * (whole-game patterns) and team-specific groups when both exist.
 *
 * These are presented strictly as FORM insights — NOT betting. No odds, no
 * stake, no bet wording anywhere. Self-hides when there are no trends so an
 * unsynced or earlier-round fixture renders nothing.
 */
export function MatchTrends({ fixtureId }: { fixtureId: string }) {
  const palette = useThemePalette();
  const { t } = useTranslation();
  const { data: xg } = useMatchXg(fixtureId);

  const { general, team } = useMemo(() => {
    const trends = xg?.stats?.trends ?? [];
    return {
      general: trends.filter((trend) => trend.isGeneralGameBet),
      team: trends.filter((trend) => !trend.isGeneralGameBet),
    };
  }, [xg?.stats?.trends]);

  if (general.length === 0 && team.length === 0) return null;

  return (
    <View className="mb-4 px-4">
      <Card className="gap-4 px-4 py-4">
        <View className="flex-row items-center gap-2">
          <Ionicons name="trending-up" size={16} color={palette.voltBright} />
          <AppText variant="caption" weight="bold" className="text-ink">
            {t('match.trendsTitle')}
          </AppText>
        </View>

        {team.length > 0 ? (
          <View className="gap-3">
            {general.length > 0 ? (
              <AppText variant="micro" weight="bold" className="uppercase tracking-widest text-muted">
                {t('match.trendsTeam')}
              </AppText>
            ) : null}
            {team.map((trend) => (
              <TrendRow key={trend.id} trend={trend} />
            ))}
          </View>
        ) : null}

        {general.length > 0 ? (
          <View className="gap-3">
            <AppText variant="micro" weight="bold" className="uppercase tracking-widest text-muted">
              {t('match.trendsGeneral')}
            </AppText>
            {general.map((trend) => (
              <TrendRow key={trend.id} trend={trend} />
            ))}
          </View>
        ) : null}
      </Card>
    </View>
  );
}
